/* eslint-disable max-statements */

import { MantineTheme } from '@mantine/core';
import { IconBellRingingFilled, IconCancel, IconFlaskFilled, IconRosetteDiscountCheckFilled } from '@tabler/icons-react';
import moment from 'moment';

import { Batch, BatchBrokerStatusEnum, BatchStatusEnum, BatchTypeEnum, EnvironmentCountries, TransactionTypes } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { IndicatorMark, TableActionEnum, TableActions } from '@components';
import { BATCH_LIST_HOURS, VIEW_DETAILS } from '@constants';
import { BatchesActions } from '@enums';
import { BatchProgressIndicator } from '../BatchProgressIndicator';
import { isUtbReportAvailable, reportModalManager } from '../UTBReport';
import { canDisplayRiskAnalysisEdit, decideIfSendAllButtonIsAvailable, deleteAllowed } from './helper';

const canUpdateHV = (row: Batch, country: string, isForwarder: boolean): boolean => {
  if (isForwarder && row.status.brokerStatus === BatchBrokerStatusEnum.SENT) {
    return false;
  }

  if (country === EnvironmentCountries.NL && row.statistics?.preAccepted > 0) {
    return false;
  }

  if (row.statistics.rejected > 0 || row.statistics.notSent > 0) {
    return true;
  }

  if (
    row.statistics.sent === 0 &&
    row.statistics.beGateSent === row.statistics.beGateReleased &&
    (row.status.value !== BatchStatusEnum.SUCCESS || row.statistics?.sent !== row.statistics?.total)
  ) {
    return true;
  }

  if (row.status.value === BatchStatusEnum.SUCCESS && (row.statistics?.accepted > 0 || row.statistics?.released > 0)) {
    return true;
  }

  return false;
};

export const buildColumnsActions = ({
  handleTableAction,
  declarant,
  settings,
  batchView,
  row,
  index,
  tableActionsList,
  secondaryActionsList,
  progress,
  user,
  theme,
  setImmediateResponse,
}: {
  handleTableAction: any;
  declarant: any;
  settings: any;
  batchView: any;
  row: any;
  index: any;
  tableActionsList: any[];
  secondaryActionsList: any[];
  progress: any;
  user: any;
  theme: MantineTheme;
  setImmediateResponse: { mutateAsync: (args: any) => Promise<any> };
}): any => {
  const batchProgress = progress.get(row._id);

  const sendToSimulator = async (batch: Batch, responseType: 'acknowledge' | 'rejected') => {
    await setImmediateResponse.mutateAsync({
      params: {
        batchId: batch._id,
      },
      body: {
        responseType,
      },
    });
    handleTableAction(TableActionEnum.Send, batch);
  };

  const sendToSimulatorBtn = (batch: Batch) => {
    const simulatorOptions = [
      {
        iconComponent: <IconRosetteDiscountCheckFilled color={theme.colors.green[6]} />,
        text: 'Acknowledge',
        onClick: () => sendToSimulator(batch, 'acknowledge'),
      },
      {
        iconComponent: <IconCancel color={theme.colors.red[6]} />,
        text: 'Technical Rejection',
        responseType: 'rejected' as const,
        onClick: () => sendToSimulator(batch, 'rejected'),
        disabled: settings.country === EnvironmentCountries.NL,
      },
    ];

    return {
      iconComponent: (
        <IndicatorMark>
          <img src={IconFactory.send} alt="Send to simulator" />
        </IndicatorMark>
      ),
      multi: true,
      options: simulatorOptions,
      text: 'Send to simulator',
    };
  };

  if (!settings.isProd) {
    if (!row.useCustomsSimulator && row.statistics.sent === 0) {
      tableActionsList.push({
        iconComponent: <IconFlaskFilled size={20} color={theme.colors.blue[6]} />,
        text: 'Enable Simulator Mode',
        onClick: () => handleTableAction(TableActionEnum.EnableSimulator, row),
      });
    }

    if (row.useCustomsSimulator && !decideIfSendAllButtonIsAvailable(row, batchView, declarant, settings)) {
      tableActionsList.push({
        iconComponent: (
          <IndicatorMark>
            <IconBellRingingFilled color={theme.colors.blue[6]} size={20} />
          </IndicatorMark>
        ),
        text: 'Simulate Messages',
        onClick: () => handleTableAction(TableActionEnum.SimulateMessages, row),
      });
    }
  }

  if (['invalidating-declarations'].includes(progress.get(row._id)?.status)) {
    return (
      <BatchProgressIndicator
        status={batchProgress?.status}
        value={batchProgress?.value}
        processedUrls={batchProgress?.processedUrls}
        totalWebUrls={batchProgress?.totalWebUrls}
      />
    );
  }

  if (progress.has(row._id)) {
    return <BatchProgressIndicator status={progress.get(row._id)?.status} value={progress.get(row._id)?.value} />;
  }

  if (
    [BatchStatusEnum.IN_PROGRESS, BatchStatusEnum.SCRAPING, BatchStatusEnum.SENDING].includes(row.status.value as BatchStatusEnum) ||
    (row.status.value === BatchStatusEnum.SUCCESS && row.status.subStatus === 'analysis')
  ) {
    secondaryActionsList = [];
    // if more than 3 hours have passed since the batch is in processing, show the delete button
    if (moment(row.updatedAt).add(BATCH_LIST_HOURS, 'hour') < moment()) {
      tableActionsList.push({
        icon: IconFactory.delete,
        text: 'Delete',
        onClick: () => handleTableAction(BatchesActions.DELETE, row),
      });
    } else if (row.status.value === BatchStatusEnum.SENDING && row.statistics?.total > 0) {
      const sent = row.statistics.sent ?? 0;
      const percent = Math.floor((sent / row.statistics.total) * 100);
      return <BatchProgressIndicator status="sending-declarations" value={percent} displayValue={percent === 0 ? ' ' : undefined} />;
    } else if (batchProgress) {
      return <BatchProgressIndicator status={batchProgress?.status} value={batchProgress?.value} />;
    }
  } else if (row.status.value === 'failed') {
    tableActionsList.push({
      icon: IconFactory.delete,
      text: 'Delete',
      onClick: () => handleTableAction(TableActionEnum.Delete, row),
    });
    secondaryActionsList.push({
      icon: IconFactory.update,
      text: 'Update',
      onClick: () => handleTableAction(TableActionEnum.Update, row),
    });
  } else {
    if (batchProgress?.status === 'screenshot-in-progress') {
      return (
        <BatchProgressIndicator
          status={batchProgress?.status}
          value={batchProgress?.value}
          processedUrls={batchProgress?.processedUrls}
          totalWebUrls={batchProgress?.totalWebUrls}
        />
      );
    }

    if ([BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(batchView)) {
      if (canDisplayRiskAnalysisEdit(row)) {
        tableActionsList.push({
          icon: IconFactory.refactor,
          text: 'Update Risk Analysis',
          onClick: () => handleTableAction(TableActionEnum.ExecuteRiskAnalysis, row),
        });
      }

      if (!(settings.isForwarder && !user?.isSupervisor)) {
        secondaryActionsList.push({
          icon: IconFactory.riskAnalysisReport,
          text: 'Generate risk analysis report',
          onClick: () => handleTableAction(TableActionEnum.GenerateRiskAnalysisReport, row),
        });
      }
      secondaryActionsList.push({
        icon: IconFactory.sendRiskAnalysisToCustomer,
        text: 'Generate customer report',
        onClick: () => handleTableAction(TableActionEnum.GenerateCustomerReport, row),
      });

      if (canUpdateHV(row, settings.country, settings.isForwarder)) {
        secondaryActionsList.push({
          icon: IconFactory.update,
          text: 'Update',
          onClick: () => handleTableAction(TableActionEnum.Update, row),
        });
      }

      if (row.status.value === BatchStatusEnum.SUCCESS) {
        secondaryActionsList.push({
          icon: IconFactory.finalize,
          text: 'Finalize',
          onClick: () => handleTableAction(TableActionEnum.Finalize, row),
        });
      }
      if (!settings.isForwarder) {
        if (declarant?.hasInvoiceTemplate && row.statistics.released === row.statistics.total) {
          secondaryActionsList.push({
            icon: IconFactory.report,
            text: 'Generate invoice',
            onClick: () => handleTableAction(TableActionEnum.GenerateInvoice, row),
          });
        }
      }
      if (row.transactionType === TransactionTypes.B2B_MULTIPLE && settings.isForwarder) {
        secondaryActionsList.push({
          icon: IconFactory.report,
          text: 'Generate proforma B2B invoice',
          onClick: () => handleTableAction(TableActionEnum.GenerateProformaB2BInvoice, row),
        });
      }
      if (!settings.isForwarder || row.status.brokerStatus !== BatchBrokerStatusEnum.SENT) {
        secondaryActionsList.push({
          icon: IconFactory.customsSync,
          text: 'Run risk analysis calculations',
          onClick: () => handleTableAction(TableActionEnum.RiskAnalysisCalculations, row),
        });
      }

      secondaryActionsList.push({
        icon: IconFactory.viewImage,
        text: 'Export screenshots',
        onClick: () => handleTableAction(TableActionEnum.ExportScreenshots, row),
      });
    }

    if (decideIfSendAllButtonIsAvailable(row, batchView, declarant, settings)) {
      if (row.useCustomsSimulator) {
        tableActionsList.push(sendToSimulatorBtn(row));
      } else {
        tableActionsList.push({
          icon: IconFactory.send,
          text: 'Send',
          onClick: () => handleTableAction(TableActionEnum.Send, row),
        });
      }
    }
    if (deleteAllowed(row)) {
      tableActionsList.push({
        icon: IconFactory.delete,
        text: 'Delete',
        onClick: () => handleTableAction(TableActionEnum.Delete, row),
      });
    }

    if (
      ![BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(batchView) &&
      !(settings.isForwarder && row.status.brokerStatus === BatchBrokerStatusEnum.SENT)
    ) {
      secondaryActionsList.push({
        icon: IconFactory.update,
        text: 'Update',
        onClick: () => handleTableAction(TableActionEnum.Update, row),
      });
    }

    tableActionsList.push({
      icon: IconFactory.archive,
      text: 'Archive',
      onClick: () => handleTableAction(TableActionEnum.Archive, row),
    });

    if (row.virtuals.hasFileInCloud) {
      secondaryActionsList.push({
        icon: IconFactory.download,
        text: 'Download file',
        onClick: () => handleTableAction(TableActionEnum.DownloadFile, row),
      });
    }

    if (!settings.isForwarder) {
      if (batchView === BatchTypeEnum.LOW_VALUES || row.statistics?.sent > 0 || row.statistics?.preSent > 0 || row.statistics?.beGateSent > 0) {
        secondaryActionsList.push({
          icon: IconFactory.customsSync,
          text: 'Customs sync',
          onClick: () => handleTableAction(TableActionEnum.CustomsSync, row),
        });
      }

      secondaryActionsList.push({
        icon: IconFactory.report,
        text: 'Generate customs report',
        onClick: () => handleTableAction(TableActionEnum.GenerateCustomsReport, row),
      });
    }

    if (settings.isForwarder) {
      if (row.statistics?.total > 0 && row.statistics?.total === row.statistics?.released) {
        secondaryActionsList.push({
          icon: IconFactory.report,
          text: 'Generate customs report',
          onClick: () => handleTableAction(TableActionEnum.GenerateCustomsReport, row),
        });
      }
    }
  }

  if (isUtbReportAvailable(row, settings)) {
    const isNL = settings.country === EnvironmentCountries.NL;
    const reportTitle = isNL ? 'UTB Report' : 'Customs Debt report';
    secondaryActionsList.push({
      icon: IconFactory.report,
      text: isNL ? 'Generate UTB report' : 'Customs Debt report',
      onClick: () => reportModalManager.openUTB(row._id, reportTitle),
    });
  }

  if (row.status.subStatus === BatchStatusEnum.ANALYSIS) {
    tableActionsList = [];
    secondaryActionsList = [];

    if (row.status.value === BatchStatusEnum.FAILED) {
      tableActionsList.push({
        icon: IconFactory.delete,
        text: 'Delete',
        onClick: () => handleTableAction(TableActionEnum.Delete, row),
      });
      secondaryActionsList.push(
        {
          icon: IconFactory.view,
          text: VIEW_DETAILS,
          onClick: () => handleTableAction(TableActionEnum.View, row),
        },
        {
          icon: IconFactory.update,
          text: 'Update',
          onClick: () => handleTableAction(TableActionEnum.Update, row),
        },
      );
      if (row.virtuals.hasFileInCloud) {
        secondaryActionsList.push({
          icon: IconFactory.download,
          text: 'Download file',
          onClick: () => handleTableAction(TableActionEnum.DownloadFile, row),
        });
      }
    }
  }

  if (row.trace?.length > 0) {
    secondaryActionsList.push({
      icon: IconFactory.inspect,
      text: 'View audit',
      onClick: () => handleTableAction(TableActionEnum.BatchAudit, row),
    });
  }

  if (row.status.value === BatchStatusEnum.SENDING) {
    tableActionsList = [];
    secondaryActionsList = [];
  }

  return (
    <TableActions
      actions={tableActionsList}
      secondaryActions={secondaryActionsList}
      showDebugMenu={true}
      batchView={batchView}
      data-pw={row.name}
      // batchId is only used for generating test files
      batchdId={row._id}
    />
  );
};
